import './index.css'

import cn from 'clsx'
import React, { useRef, useState } from 'react'
import type { ConnectedProps } from 'react-redux'
import { connect } from 'react-redux'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import {
  CUSTOM_FORMAT_SETTINGS,
  METADATA_ALIAS_BATCH_EDITING,
  METADATA_ALIAS_INDIVIDUAL_EDITING,
  SHOWCASES,
} from '@disco/constants/FeatureTypes'
import { sendMixpanelEvent } from '@disco/state-management/actions/AnalyticsActions'
import {
  saveShortlist,
  updateShortlist,
} from '@disco/state-management/actions/ShortlistActions'
import { isDeviceTypePhone } from '@disco/state-management/selectors/app'
import {
  makeGetFeatureData,
  makeIsFeatureEnabled,
} from '@disco/state-management/selectors/business'
import { getUserTerritories } from '@disco/state-management/selectors/groups'
import captureError from '@disco/utils/captureError'
import {
  getShortlistPresentationType,
  hasShortlistContainMainTrack,
  isFormatsTabDisabled,
  removeBinaryImagesInShortlist,
} from '@disco/utils/shortlist'

import type { Shortlist } from 'types/shortlist'

import { getShortlistBaseRoute } from 'utils/routing'

import DocumentHeadTags from 'components/document-head-tags'
import Button from 'components/ui/button'
import ButtonContainer from 'components/ui/button-container'
import DiscoModal from 'components/ui/disco-modal'
import Stack from 'components/ui/stack'
import Tabs, { Tab } from 'components/ui/tabs'

import ShortlistSave from 'pages/explorer/shortlists/save'
import { ConnectedShortlistMetadataEdit } from 'pages/explorer/shortlists/save/playlist-metadata-edit'
import PlaylistPresentation from 'pages/explorer/shortlists/save/playlist-presentation'
import {
  METADATA_TAB_IDX,
  PRESENTATION_TAB_IDX,
  SAVE_TAB_IDX,
} from 'pages/explorer/shortlists/save/playlist-save-modal/constants'

function mapStateToProps(state) {
  const getFeatureData = makeGetFeatureData()
  const {
    isFeatureEnabled: customFormatsEnabled,
    isTrial,
    badgeTitle,
  } = getFeatureData(state, {
    featureName: CUSTOM_FORMAT_SETTINGS,
  })
  const isFeatureEnabledSelector = makeIsFeatureEnabled()
  const metadataAliasBatchEditingEnabled = isFeatureEnabledSelector(state, {
    featureName: METADATA_ALIAS_BATCH_EDITING,
  })
  const metadataAliasIndividualEditingEnabled = isFeatureEnabledSelector(
    state,
    {
      featureName: METADATA_ALIAS_INDIVIDUAL_EDITING,
    },
  )
  const showcasesEnabled = isFeatureEnabledSelector(state, {
    featureName: SHOWCASES,
  })

  return {
    defaultTerritory: getUserTerritories(state)[0],
    tracks: state.tracks,
    customFormatsEnabled,
    isTrial,
    badgeTitle,
    metadataAliasBatchEditingEnabled,
    metadataAliasIndividualEditingEnabled,
    showcasesEnabled,
    isPhone: isDeviceTypePhone(state),
  }
}

const mapDispatchToProps = {
  saveShortlist,
  sendMixpanelEvent,
  updateShortlist,
}

const connector = connect(mapStateToProps, mapDispatchToProps)

type ReduxProps = ConnectedProps<typeof connector>

type Props = ReduxProps & {
  tabIndex: number
  children: React.ReactNode
  shortlist: Shortlist
}

const PlaylistSaveModal = (props: Props) => {
  const location = useLocation()
  const params = useParams<{ shortlistId: string }>()
  const navigate = useNavigate()
  const bottomRowRef = useRef()
  const [imageFile, setImageFile] = useState()

  const playlistSettings = location.state?.location === 'playlist_settings'

  const closeModal = () => {
    const { shortlist, updateShortlist } = props
    updateShortlist(
      shortlist.id,
      removeBinaryImagesInShortlist<Shortlist>(shortlist),
    )

    // Use returnPath from location state, fallback to home
    const returnPath = location?.state?.returnPath || '/'
    navigate(returnPath)
  }

  const handleTabChange = idx => {
    const { shortlist } = props
    const shortlistId = shortlist.id

    const baseRoute = getShortlistBaseRoute(location)

    switch (idx) {
      case SAVE_TAB_IDX:
        navigate(`${baseRoute}/shortlists/${shortlistId}/save`, {
          state: location.state,
        })
        return

      case PRESENTATION_TAB_IDX:
        navigate(`${baseRoute}/shortlists/${shortlistId}/save/presentation`, {
          state: location.state,
        })
        return

      case METADATA_TAB_IDX:
        navigate(`${baseRoute}/shortlists/${shortlistId}/metadata`, {
          state: location.state,
        })
        return
    }
  }

  const handleSubmit = async event => {
    event.preventDefault()
    const { shortlist, defaultTerritory } = props

    if (shortlist.groups.length === 0) {
      captureError(new Error('PlaylistSaveModal: shortlist has no groups'), {
        extra: {
          level: 'warning',
          shortlist_groups: shortlist.groups,
        },
      })
    }

    const shortlistId = shortlist.id
    const baseRoute = getShortlistBaseRoute(location)

    if (
      props.tabIndex === SAVE_TAB_IDX ||
      props.tabIndex === PRESENTATION_TAB_IDX
    ) {
      await props.saveShortlist(shortlistId, {
        defaultTerritory,
        imageFile,
      })
      // Navigate to saved modal
      navigate(`${baseRoute}/shortlists/${shortlistId}/saved`, {
        state: {
          returnPath: location?.state?.returnPath || '/',
        },
      })
    } else {
      // Handle metadata tab case
      const presentationType = getShortlistPresentationType(shortlist)
      props.sendMixpanelEvent(`Save playlist as ${presentationType}`)
      handleTabChange(SAVE_TAB_IDX)
    }
  }

  const renderTabContent = ({ tabNode, renderIndex }) => {
    return (
      <div
        key={renderIndex}
        className={cn({
          'PlaylistSaveModal_Tab--Hidden': props.tabIndex !== renderIndex,
        })}
      >
        {tabNode}
      </div>
    )
  }

  const renderTabsContent = () => {
    const { customFormatsEnabled, tracks, tabIndex, shortlist, isTrial } = props
    const customFormatsNotApplicable =
      isFormatsTabDisabled(shortlist, tracks) &&
      !hasShortlistContainMainTrack(shortlist)
    return [
      {
        tabNode: (
          <ShortlistSave
            navigate={navigate}
            params={params as { shortlistId: string }}
            shortlistId={params.shortlistId!}
            playlistId={shortlist?.playlist_id?.toString()}
          />
        ),
        renderIndex: SAVE_TAB_IDX,
      },
      {
        tabNode: <PlaylistPresentation />,
        renderIndex: PRESENTATION_TAB_IDX,
      },
      {
        tabNode: (
          <ConnectedShortlistMetadataEdit
            navigate={navigate}
            params={params}
            bottomRowRef={METADATA_TAB_IDX === tabIndex ? bottomRowRef : null}
            tabOpen={METADATA_TAB_IDX === tabIndex}
            updateArtwork={setImageFile}
          />
        ),
        renderIndex: METADATA_TAB_IDX,
      },
    ].map(renderTabContent)
  }

  const renderButtons = (saveCaption, saveProgress) => {
    const { shortlist, showcasesEnabled } = props
    let disabledSave = false

    if (shortlist.showcase?.type && !showcasesEnabled) {
      disabledSave = true
    }

    return (
      <ButtonContainer>
        <Button onClick={closeModal} caption="Cancel" />
        <Button
          appearance="primary"
          caption={saveCaption}
          progressCaption={saveProgress}
          progressIndicator={shortlist.saving}
          disabled={disabledSave}
          type="submit"
        />
      </ButtonContainer>
    )
  }

  const {
    tabIndex: idx,
    children,
    metadataAliasBatchEditingEnabled,
    metadataAliasIndividualEditingEnabled,
    isPhone,
  } = props
  let saveCaption, saveProgress

  switch (idx) {
    case SAVE_TAB_IDX:
      saveCaption = 'Save Playlist'
      saveProgress = 'Saving Playlist'
      break

    case PRESENTATION_TAB_IDX:
      saveCaption = 'Save'
      saveProgress = 'Saving'
      break

    case METADATA_TAB_IDX:
      saveCaption = 'Save Metadata'
      saveProgress = 'Saving Metadata'
      break
  }

  const metadataEditingDisabled =
    !metadataAliasBatchEditingEnabled && !metadataAliasIndividualEditingEnabled
  return (
    <DiscoModal onRequestClose={closeModal}>
      <form className="ShortlistSaveModal" onSubmit={handleSubmit}>
        <DocumentHeadTags title="Save playlist | DISCO" />
        {playlistSettings ? (
          <h1 className="Form_Header">Playlist Settings</h1>
        ) : (
          <h1 className="Form_Header">Save Playlist</h1>
        )}
        <Tabs className="Tabs--modal" index={idx} onChange={handleTabChange}>
          <Tab label="Playlist Info" />
          <Tab label="Presentation" />
          <Tab
            className={cn({
              'Tab--Hidden': metadataEditingDisabled || isPhone,
            })}
            label="Alias Metadata"
          />
        </Tabs>

        <Stack space="xxsmall">
          {renderTabsContent()}
          <div className="SavePlaylist_BottomBtnCont">
            <div ref={bottomRowRef} />
            <div className="SavePlaylist_BottomRowButton" />
            {renderButtons(saveCaption, saveProgress)}
          </div>
        </Stack>
      </form>

      {children}
    </DiscoModal>
  )
}

export default connector(PlaylistSaveModal)
